export const AGENDA_BUCKETS = ['amarelos', 'laranjas', 'vermelhos'];
const AGENDA_ACTION_KINDS = ['details', 'nucleo', 'status'];

const bucketRank = bucket => {
  const index = AGENDA_BUCKETS.indexOf(bucket);
  return index === -1 ? AGENDA_BUCKETS.length : index;
};

const sortDeadlines = deadlines =>
  [...deadlines].sort(
    (left, right) => bucketRank(left.bucket) - bucketRank(right.bucket)
  );

const caseBucketRank = postSaleCase =>
  bucketRank(sortDeadlines(postSaleCase.deadlines)[0]?.bucket);

export const presentAgendaAddress = address => {
  const street = address?.logradouro || address?.endereco;
  if (!street) return '';

  let label = String(street).replace('Rua', 'R.').replace('Avenida', 'Av.');
  if (address.numero) label += `, ${address.numero}`;
  if (address.unidade) label += ` - ${address.unidade}`;
  if (address.complemento) label += ` - ${address.complemento}`;
  if (address.cidade) label += ` - ${address.cidade}`;
  if (address.uf) label += ` / ${address.uf}`;
  return label;
};

const isValidDate = value =>
  value instanceof Date && !Number.isNaN(value.getTime());

const startOfDay = value => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

export const parseAgendaDate = value => {
  if (!value) return null;
  const date = new Date(value);
  return isValidDate(date) ? date : null;
};

export const dateKey = value => {
  const date = parseAgendaDate(value);
  if (!date) return '';

  return [date.getFullYear(), date.getMonth() + 1, date.getDate()]
    .map((part, index) => (index ? String(part).padStart(2, '0') : part))
    .join('-');
};

const sourceActions = postSaleCase => {
  const actions =
    postSaleCase.agenda_actions || postSaleCase.status_payload?.agenda_actions;
  if (Array.isArray(actions)) return actions;
  if (actions && typeof actions === 'object') return [actions];
  return [];
};

const agendaActions = postSaleCase =>
  sourceActions(postSaleCase)
    .filter(action => AGENDA_ACTION_KINDS.includes(action?.kind))
    .map(action => ({
      ...action,
      code: action.code || action.kind,
    }));

const actionForBucket = (actions, bucket) =>
  actions.find(action => !action.bucket || action.bucket === bucket);

const deadlineMessage = (postSaleCase, deadline) => {
  const values = {
    days: Math.abs(Number(deadline.days_remaining || 0)),
    status: postSaleCase.status_label,
    ...(deadline.message || {}),
  };

  if (deadline.bucket === 'vermelhos') {
    return { key: 'POST_SALE.DEADLINES.MESSAGE_OVERDUE', values };
  }
  if (deadline.bucket === 'laranjas') {
    return { key: 'POST_SALE.DEADLINES.MESSAGE_DUE_SOON', values };
  }

  return { key: 'POST_SALE.DEADLINES.MESSAGE_ATTENTION', values };
};

const sourceDeadlines = postSaleCase => {
  if (Array.isArray(postSaleCase.deadlines)) return postSaleCase.deadlines;
  return postSaleCase.deadline ? [postSaleCase.deadline] : [];
};

const presentDeadline = (postSaleCase, deadline, actions) => {
  const date = parseAgendaDate(deadline.due_at || postSaleCase.status_due_at);

  if (!date || !AGENDA_BUCKETS.includes(deadline.bucket)) return null;

  const legacyAction = deadline.code?.startsWith('checklist:')
    ? undefined
    : actionForBucket(actions, deadline.bucket);

  return {
    ...deadline,
    action: deadline.action || legacyAction,
    due_at: deadline.due_at || postSaleCase.status_due_at,
    message: deadlineMessage(postSaleCase, deadline).values,
  };
};

export const presentAgendaCase = postSaleCase => {
  const actions = agendaActions(postSaleCase);
  const groups = sourceDeadlines(postSaleCase).reduce((entries, deadline) => {
    const presentable = presentDeadline(postSaleCase, deadline, actions);
    if (!presentable) return entries;

    const date = parseAgendaDate(presentable.due_at);
    const key = dateKey(date);
    entries[key] ||= { agenda_date: date, deadlines: [] };
    entries[key].deadlines.push(presentable);
    return entries;
  }, {});

  return Object.values(groups).map(group => ({
    ...postSaleCase,
    ...group,
    deadlines: sortDeadlines(group.deadlines),
    agenda_date_key: dateKey(group.agenda_date),
  }));
};

export const presentAgendaCases = cases =>
  cases
    .flatMap(presentAgendaCase)
    .filter(postSaleCase => postSaleCase.agenda_date_key)
    .sort(
      (left, right) =>
        left.agenda_date - right.agenda_date ||
        caseBucketRank(left) - caseBucketRank(right)
    );

export const countDeadlines = cases =>
  cases.reduce(
    (counts, postSaleCase) => {
      postSaleCase.deadlines.forEach(deadline => {
        counts[deadline.bucket] += 1;
      });
      return counts;
    },
    { amarelos: 0, laranjas: 0, vermelhos: 0 }
  );

export const casesForDate = (cases, value, enabledBuckets) => {
  const selectedKey = dateKey(value);
  return cases.flatMap(postSaleCase => {
    if (postSaleCase.agenda_date_key !== selectedKey) return [];

    const deadlines = postSaleCase.deadlines.filter(
      deadline => enabledBuckets[deadline.bucket]
    );
    return deadlines.length ? [{ ...postSaleCase, deadlines }] : [];
  });
};

export const countsForDate = (cases, value) =>
  countDeadlines(
    cases.filter(
      postSaleCase => postSaleCase.agenda_date_key === dateKey(value)
    )
  );

export const calendarCells = (value, cases) => {
  const selectedDate = startOfDay(value);
  const year = selectedDate.getFullYear();
  const month = selectedDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const monthLength = new Date(year, month + 1, 0).getDate();
  const leadingDays = firstDay.getDay();
  const cellCount = Math.ceil((leadingDays + monthLength) / 7) * 7;

  return Array.from({ length: cellCount }, (_, index) => {
    const offset = index - leadingDays;
    const date = new Date(year, month, offset + 1);
    return {
      counts: countsForDate(cases, date),
      date,
      day: date.getDate(),
      key: dateKey(date),
      outsideMonth: date.getMonth() !== month,
    };
  });
};

export const shiftMonth = (value, offset) => {
  const date = startOfDay(value);
  const day = date.getDate();
  date.setDate(1);
  date.setMonth(date.getMonth() + offset);
  const lastDay = new Date(
    date.getFullYear(),
    date.getMonth() + 1,
    0
  ).getDate();
  date.setDate(Math.min(day, lastDay));
  return date;
};

export const dateFromMonthInput = (value, selectedDate) => {
  const [year, month] = value.split('-').map(Number);
  if (!year || !month) return selectedDate;

  const date = new Date(year, month - 1, 1);
  date.setDate(
    Math.min(
      selectedDate.getDate(),
      new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
    )
  );
  return date;
};
